import React, { useEffect } from "react";
import "./index.scss";
import "./news-lead.scss";
import "./news-lead-v2.scss";
import { story } from "./types";
import {
  setResponsiveImage,
  checkImage,
  formatDate,
  imgCrop,
  isParalympics,
  isSpecialEvent
} from "../../helpers";
import Youtube from "../YouTube";
import { themeListener } from "../../static/Theming";

interface link {
  name: string;
  url: string;
}

const Feed = ({
  newsItems,
  viewMoreLink,
  viewMoreCtaText = "Read more",
  spliceIndex = 4
}: {
  newsItems: story[];
  viewMoreLink: link;
  viewMoreCtaText?: null | string;
  spliceIndex?: number;
}) => {
  return (
    <div className="news-feed__wrapper">
      {viewMoreLink ? (
        <h2 className="news-feed__title">
          <a href={viewMoreLink.url} className="">
            Latest News
          </a>
        </h2>
      ) : null}
      <div className="news-feed">
        {newsItems
          .sort(
            (a: story, b: story) =>
              new Date(b.displayDate).getTime() -
              new Date(a.displayDate).getTime()
          )
          .splice(0, spliceIndex)
          .map((story: story) => (
            <div
              className={`news-feed__item${
                story.imageUrls.length ? "" : " no-image"
              }`}
              key={story.id}
            >
              <div className="news-feed__item-blurb">
                <h3>
                  <a href={story.articleUrl}>{story.title}</a>
                </h3>
                <a className="btn" href={story.articleUrl}>
                  {viewMoreCtaText}
                </a>
                <div className="news-feed__meta">
                  <span className="news-feed__post-time">
                    {formatDate(story.displayDate).shortNoYear}
                  </span>
                  {story && story.tags
                    ? story.tags.map((tag: string, key: number) => (
                        <a
                          key={key}
                          className="news-feed__tag"
                          href={`${story.listingPageUrl}?categoryId=${story.category}&tag=${tag}`}
                        >
                          {tag}
                        </a>
                      ))
                    : null}
                </div>
              </div>
              {story.imageUrls.length ? (
                <div
                  className="news-feed__item-image"
                  style={{
                    backgroundImage: `url("${process.env.HOST}${checkImage({
                      images: story.imageUrls,
                      size: "Small"
                    })}")`
                  }}
                ></div>
              ) : null}
            </div>
          ))}
      </div>
      {viewMoreLink ? (
        <div className="news-feed__link flex-container">
          <a href={viewMoreLink.url} className="news-feed__button btn">
            {viewMoreCtaText}
          </a>
        </div>
      ) : null}
    </div>
  );
};

export default (props: {
  newsItems: story[];
  title: string;
  backgroundImage: imgCrop[];
  videoId: string;
  startTime: number;
  alignment: string;
  textAlign: string;
  subtext: string;
  viewMoreLink: link;
  linkUrl: string;
  linkTarget?: string;
  viewMoreCtaText?: string | null;
  ctaText: string | null;
  olympics?: boolean;
  version?: number;
}) => {
  let {
    backgroundImage,
    videoId,
    startTime,
    alignment,
    textAlign,
    subtext,
    ctaText = "",
    title,
    linkUrl,
    linkTarget = "_self",
    olympics,
    version = 0
  } = props;

  const isOlympics =
    location.pathname.indexOf("lympi") >= 0 ||
    location.pathname.indexOf("limpi") >= 0 ||
    isSpecialEvent;
  version = isOlympics || olympics ? 2 : 0;

  useEffect(() => {
    if (!videoId || videoId.length === 0) {
      themeListener({
        callback: ({ layoutVersion }) => {
          setResponsiveImage({
            mobileImage: checkImage({
              images: backgroundImage,
              size:
                version == 2 || [2, 3].includes(layoutVersion)
                  ? "Medium"
                  : "Small"
            }),
            tabletImage: checkImage({
              images: backgroundImage,
              size: "Medium"
            }),
            desktopImage: checkImage({
              images: backgroundImage,
              size: "Large"
            }),
            elClassName: `news-lead__cta-wrapper--main`
          });
        }
      });
    }
  }, []);

  const LeadStory = () => (
    <a
      href={linkUrl ? linkUrl : "javascript:void(0)"}
      target={linkTarget}
      className={`news-lead__hero`}
    >
      <div
        className={`news-lead__cta-wrapper news-lead__cta-wrapper--main${
          textAlign ? ` align--${textAlign}` : ""
        }`}
      >
        {videoId ? (
          <div className="news-lead__video">
            <Youtube
              id={videoId}
              onEnd={() => {}}
              onReady={({ target }) => {
                target.mute();
                target.seekTo(startTime ? startTime : 0);
                target.setLoop(true);
                const delay = 1000;
                setTimeout(() => {
                  const isLive = target.playerInfo.videoData.isLive;
                  if (isLive) return;
                  // create loop
                  setInterval(() => {
                    target.seekTo(startTime ? startTime : 0);
                  }, (startTime ? target.getDuration() - delay - startTime : target.getDuration()) * 1000);
                }, delay);
              }}
            />
          </div>
        ) : null}
        <div className="news-lead__cta">{subtext}</div>
        <h6 className="news-lead__cta-heading">{title}</h6>
        {ctaText ? (
          <span className="news-lead__cta-text">{ctaText}</span>
        ) : null}
      </div>
    </a>
  );

  const Comp = () => (
    <div
      className={`${isOlympics || olympics ? "oly " : ""}${
        isParalympics ? "paralympics " : ""
      }news-lead${alignment ? ` align--${alignment}` : ""}`}
    >
      <LeadStory />
      <div className="mobile news-lead__hero-heading-text">
        <a href={linkUrl ? linkUrl : "javascript:void(0)"} className=" ">
          <h6 className="news-lead__cta-heading">{title}</h6>
        </a>
      </div>

      <Feed
        {...props}
        spliceIndex={isOlympics || olympics || isParalympics ? 3 : 4}
      />
    </div>
  );

  return (
    <section
      className={`page-section${
        isOlympics || olympics || isParalympics ? " olympics v2" : ""
      }`}
    >
      <section className="page-section__inner">
        <Comp />
      </section>
    </section>
  );
};
